import { useCallback, useRef, useState } from "react";
import { clsx } from "clsx";
import { twMerge } from "tailwind-merge";
import { X, Check, Pencil } from "lucide-react";

import { Input } from "../Input/Input";
import { LoadingSpinner } from "../LoadingSpinner/LoadingSpinner";

export interface ToggledEditableBlockProps {
  className?: string;
  onNewValue?: (value: string) => Promise<void>;
  children: string;
}

export function ToggledEditableBlock({
  className,
  children,
  onNewValue,
}: ToggledEditableBlockProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const startEdit = useCallback(() => {
    setIsEditing(true);
  }, []);

  const handleCancel = useCallback(() => {
    setIsEditing(false);
  }, []);
  const handleSave = useCallback(async () => {
    const value = inputRef.current?.value;
    if (!value || value === children) {
      handleCancel();
    } else {
      setIsLoading(true);
      await onNewValue?.(value);
      setIsEditing(false);
      setIsLoading(false);
    }
  }, [children, handleCancel, onNewValue]);

  const inputRef = useRef<HTMLInputElement>(null);

  return (
    <div className={twMerge(clsx("inline-flex items-center gap-4", className))}>
      {isEditing ? (
        <Input
          defaultValue={children}
          className="text-sm"
          autoFocus
          ref={inputRef}
          trailingIcon={
            isLoading ? (
              <LoadingSpinner className="size-5" />
            ) : (
              <>
                <button
                  onClick={handleCancel}
                  className="text-textSubtle hover:text-textDefault cursor-pointer"
                >
                  <X size={20} />
                </button>
                <button
                  onClick={handleSave}
                  className="text-interactive-selected cursor-pointer hover:text-blue-600"
                >
                  <Check size={20} />
                </button>
              </>
            )
          }
        />
      ) : (
        <span contentEditable={isEditing}>{children}</span>
      )}
      <div className="flex items-center gap-2 text-sm font-normal">
        {!isEditing && (
          <button
            onClick={startEdit}
            className="text-textSubtle hover:text-textDefault cursor-pointer"
          >
            <Pencil size={20} />
          </button>
        )}
      </div>
    </div>
  );
}
